import i18n from "../../i18n";
export { formatEta } from "../../utils/progressEta";

const FRESHDESK_SUPPORT_URL = "https://offolio.freshdesk.com/support/home";

const tr = (key, defaultValue, values) => i18n.t(key, { ...values, defaultValue });

const refreshAndRetryHint = () =>
  tr("progressNotifications.hints.refreshAndRetry", "Refresh the page, then retry manually. If the issue persists, check the documentation and contact support.");

const retryAndSupportHint = () =>
  tr("progressNotifications.hints.retryAndSupport", "Retry manually. If the issue persists, check the documentation and contact support.");

function defaultFallback(kind) {
  const t = i18n.t.bind(i18n);
  const project = t("term.project").toLowerCase();
  switch (kind) {
    case "delete":
      return {
        message: tr("progressNotifications.errors.deleteFallback", `We couldn't delete this ${project}.`, { project }),
        actionHint: refreshAndRetryHint(),
      };
    case "scenarioDelete":
      return {
        message: tr("progressNotifications.errors.scenarioDeleteFallback", "We couldn't delete this scenario."),
        actionHint: refreshAndRetryHint(),
      };
    case "updateUsages":
      return {
        message: "We couldn't update the usages.",
        actionHint: refreshAndRetryHint(),
      };
    case "copy":
      return {
        message: "We couldn't copy the scenarios.",
        actionHint: retryAndSupportHint(),
      };
    case "mppBatchImport":
      return {
        message: "We couldn't learn from one or more of your files.",
        actionHint: "Check that your files are valid MPP/XML/MPX schedules, then retry manually. If the issue persists, check the documentation and contact support.",
      };
    case "aiImport":
      return {
        message: "We couldn't finish importing and enriching your schedule.",
        actionHint: "Retry manually. If enrichment fails again, update usages from the Gantt view and contact support.",
      };
    case "autoFeedActuals":
      return {
        message: "We couldn't auto-feed actuals across approved scenarios.",
        actionHint: refreshAndRetryHint(),
      };
    case "resetCapacity":
      return {
        message: "We couldn't reset capacity for all resources.",
        actionHint: "Refresh the resource list, then retry manually. If the issue persists, check the documentation and contact support.",
      };
    case "generativeScheduling":
      return {
        message: "We couldn't generate the schedule.",
        actionHint: refreshAndRetryHint(),
      };
    case "portfolioSimulation":
      return {
        message: `We couldn't run the simulation for this ${project}.`,
        actionHint: retryAndSupportHint(),
      };
    case "portfolioCopy":
      return {
        message: "We couldn't create this portfolio scenario.",
        actionHint: retryAndSupportHint(),
      };
    case "import":
    default:
      return {
        message: "We couldn't import your Excel file.",
        actionHint: "Verify it matches the generated template, then retry manually. If the issue persists, check the documentation and contact support.",
      };
  }
}

function applyDiscreetManualRetry(actionHint) {
  const hint = (actionHint || "").trim();
  if (!hint) return retryAndSupportHint();
  const lower = hint.toLowerCase();
  const hasRetry =
    lower.includes("retry") ||
    lower.includes("try again") ||
    lower.includes("réessa") ||
    lower.includes("re-try");
  if (hasRetry) return hint;
  return `${hint} ${tr("progressNotifications.hints.retryIfNeeded", "Retry manually if needed.")}`;
}

/**
 * Translate the raw error string returned by the BE into a user-facing message
 * tailored to the job kind.
 *
 * Returns:
 *   message    — short, human-readable explanation (≤ 2 sentences)
 *   actionHint — concrete corrective action the user can take (shown in "What to do" section)
 *   raw        — the original backend error (shown in collapsible "Technical details")
 */
export function humanizeImportError(raw, kind = "import") {
  const isDelete = kind === "delete";
  const isUpdateUsages = kind === "updateUsages";
  const isCopy = kind === "copy";
  const isMpp = kind === "mppBatchImport";
  const isAi = kind === "aiImport";
  const isScenarioDelete = kind === "scenarioDelete";
  const isResetCapacity = kind === "resetCapacity";
  const fallbackBase = defaultFallback(kind);

  const fallback = {
    message: fallbackBase.message,
    actionHint: fallbackBase.actionHint,
    raw: raw || null,
    supportUrl: FRESHDESK_SUPPORT_URL,
  };

  if (!raw || typeof raw !== "string") return fallback;
  const lower = raw.toLowerCase();

  // ── Interrupted by a backend restart / redeploy (any job kind) ────────────
  // The owning instance died mid-run; the server resolved the job to a terminal
  // INTERRUPTED state. Not a real processing failure — just retry.
  if (
    lower.includes("interrupted") ||
    lower.includes("server was updated") ||
    lower.includes("stopped responding") ||
    lower.includes("no longer tracked")
  ) {
    return {
      message: i18n.t(
        "asyncJob.interrupted.message",
        "This operation was interrupted by a server update and didn't finish."
      ),
      actionHint: i18n.t(
        "asyncJob.interrupted.actionHint",
        "Nothing was left in a broken state — just run it again. If it keeps happening, contact support."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  if (
    isResetCapacity &&
    (
      lower.includes("stalestateexception") ||
      lower.includes("unexpected row count from update") ||
      lower.includes("batch update returned unexpected row count")
    )
  ) {
    return {
      message: "Capacity reset conflicted with a recent backend update.",
      actionHint: "Refresh the resource list to get the latest values, then retry manually.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // ── Update Usages ────────────────────────────────────────────────────────
  if (
    isUpdateUsages &&
    (
      lower.includes("empty") ||
      lower.includes("no blocks") ||
      lower.includes("no block") ||
      lower.includes("no perimeter") ||
      lower.includes("no perimètre") ||
      lower.includes("no scope") ||
      lower.includes("no périmètre")
    )
  ) {
    const scope = i18n.t("term.scope");
    return {
      message: i18n.t("progressNotifications.kinds.updateUsages.noScopesMessage", {
        scope: scope.toLowerCase(),
      }),
      actionHint: i18n.t("progressNotifications.kinds.updateUsages.noScopesAction", {
        scope,
      }),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (isUpdateUsages && (lower.includes("limit") || lower.includes("exceed"))) {
    return {
      message: `Updating this scenario would exceed your portfolio's ${i18n.t("term.block").toLowerCase()} limit.`,
      actionHint: `Reduce the quantities in your scenario or contact your administrator to raise the portfolio limit, then retry manually.`,
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // ── Scenario Copy ─────────────────────────────────────────────────────────
  if (isCopy && lower.includes("all scenario copies failed")) {
    return {
      message: "None of the selected scenarios could be copied.",
      actionHint: "Verify the scenarios still exist and that you have the required permissions, then retry.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (isCopy && (lower.includes("already exists") || lower.includes("already"))) {
    return {
      message: "A portfolio scenario with this name already exists.",
      actionHint: "Rename the portfolio scenario or delete the existing one, then retry the copy.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // ── MPP Batch Import ──────────────────────────────────────────────────────
  if (isMpp && (lower.includes("mppfilereading") || lower.includes("mpxj") || lower.includes("parse"))) {
    return {
      message: "One of your files couldn't be read — it may be corrupted or in an unsupported format.",
      actionHint: "Check that each file is a valid .mpp, .xml or .mpx schedule file. Open it in your scheduling tool, re-export it, and retry.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (isMpp && (lower.includes("license") || lower.includes("limit"))) {
    return {
      message: `Your licence limits the number of ${i18n.t("term.project").toLowerCase()}s you can learn from at once.`,
      actionHint: `Remove some files to stay within your licence limit, or contact your administrator to expand it, then retry manually.`,
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // ── AI Import ────────────────────────────────────────────────────────────
  if (isAi && (lower.includes("mlhub") || lower.includes("predict") || lower.includes("model"))) {
    return {
      message: `The AI model is temporarily unavailable. Your ${i18n.t("term.project").toLowerCase()} and schedule were imported, but enrichment didn't complete.`,
      actionHint: "The import is saved. Retry AI enrichment from the Gantt view once the service is back online.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (isAi && (lower.includes("convert") || lower.includes("automatic"))) {
    return {
      message: "The imported schedule couldn't be switched to automatic scheduling mode.",
      actionHint: "Open the scenario in the Gantt view and trigger 'Update usages' manually, then retry manually.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // ── Project Delete ────────────────────────────────────────────────────────
  if (isDelete && (lower.includes("foreign key") || lower.includes("constraint") || lower.includes("referenced"))) {
    const project = i18n.t("term.project").toLowerCase();
    const program = i18n.t("term.program").toLowerCase();
    return {
      message: tr(
        "progressNotifications.errors.deleteReferenced.message",
        `This ${project} is still referenced by other data — dependencies, ${program}s, or shared activities.`,
        { project, program }
      ),
      actionHint: tr(
        "progressNotifications.errors.deleteReferenced.actionHint",
        `Remove all references to this ${project} (check ${program}s, dependencies and shared activities), then retry the deletion.`,
        { project, program }
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (isDelete && (lower.includes("not found") || lower.includes("no such"))) {
    const project = i18n.t("term.project").toLowerCase();
    return {
      message: tr(
        "progressNotifications.errors.deleteNotFound.message",
        `The ${project} no longer exists — it may have already been deleted.`,
        { project }
      ),
      actionHint: tr(
        "progressNotifications.errors.deleteNotFound.actionHint",
        `Refresh the ${project} list. If it still appears, retry manually.`,
        { project }
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // ── Scenario Delete ───────────────────────────────────────────────────────
  if (isScenarioDelete && (lower.includes("foreign key") || lower.includes("constraint") || lower.includes("referenced"))) {
    return {
      message: tr(
        "progressNotifications.errors.scenarioDeleteReferenced.message",
        "This scenario is still referenced by a portfolio scenario or another object."
      ),
      actionHint: tr(
        "progressNotifications.errors.scenarioDeleteReferenced.actionHint",
        "Remove the scenario from any portfolio scenarios or linked objects first, then retry the deletion."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (isScenarioDelete && (lower.includes("active") || lower.includes("cannot delete"))) {
    return {
      message: tr(
        "progressNotifications.errors.scenarioDeleteActive.message",
        "This scenario is currently set as the active one and can't be deleted directly."
      ),
      actionHint: tr(
        "progressNotifications.errors.scenarioDeleteActive.actionHint",
        "Select a different scenario as active (from the scenario selector), then retry the deletion manually."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (isScenarioDelete && (lower.includes("not found") || lower.includes("no such"))) {
    return {
      message: tr(
        "progressNotifications.errors.scenarioDeleteNotFound.message",
        "The scenario no longer exists — it may have already been deleted."
      ),
      actionHint: tr(
        "progressNotifications.errors.scenarioDeleteNotFound.actionHint",
        "Refresh the page. If the scenario is still present, retry manually."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // ── Shared patterns ───────────────────────────────────────────────────────
  if (lower.includes("nullpointer") || lower === "null") {
    return {
      message: isDelete
        ? tr("progressNotifications.errors.internalDelete", "The deletion failed unexpectedly due to an internal error.")
        : tr("progressNotifications.errors.internal", "The operation failed unexpectedly due to an internal error."),
      actionHint: tr(
        "progressNotifications.errors.internalActionHint",
        "Refresh the page and try again. If the problem persists, contact your administrator and share the technical details below."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  // Project Excel import after auto-create was removed — BE returns
  // "Library structure(s) not found: <CODES>. Please import these
  // structures first ...". Pull the missing-codes list out of the
  // message and turn it into actionable guidance.
  if (lower.includes("library structure(s) not found")) {
    const match = raw.match(/Library structure\(s\) not found:\s*([^.]+)\./i);
    const codes = match ? match[1].trim() : null;
    return {
      message: codes
        ? `These scopes are missing in the library: ${codes}.`
        : "One or more scopes referenced by your file don't exist yet.",
      actionHint:
        "Open My Knowledge → My Libraries to create the missing scopes, then re-run the project import.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  // Project Excel import after BS auto-create was removed (spec §2.2 phase 2).
  if (lower.includes("breakdown structure(s) not found")) {
    const match = raw.match(/Breakdown structure\(s\) not found:\s*([^.]+)\./i);
    const codes = match ? match[1].trim() : null;
    return {
      message: codes
        ? `These structures are missing: ${codes}.`
        : "One or more structures referenced by your file don't exist yet.",
      actionHint:
        "Open My Knowledge → Structures → Excel Import to create the missing structures, then re-run the project import.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  // Project Excel import after Attribute auto-create was removed (spec §2.2 phase 3).
  if (lower.includes("input code(s) not found")) {
    const match = raw.match(/Input code\(s\) not found:\s*([^.]+)\./i);
    const codes = match ? match[1].trim() : null;
    return {
      message: codes
        ? `These inputs are missing: ${codes}.`
        : "One or more input codes referenced by your file don't exist yet.",
      actionHint:
        "Open My Knowledge → Inputs to create the missing inputs, then re-run the project import.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  // Structure XLSX import (library mode) — Phase 1 fail-fast on missing scopes.
  if (lower.includes("scope(s) not found in library")) {
    const match = raw.match(/Scope\(s\) not found in library:\s*([^.]+)\./i);
    const codes = match ? match[1].trim() : null;
    return {
      message: codes
        ? `These scopes don't exist yet: ${codes}.`
        : "One or more scope codes referenced by your file don't exist yet.",
      actionHint:
        "Open My Knowledge → My Libraries to create the missing scopes, then re-run the structure import.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (lower.includes("sheet") && (lower.includes("not found") || lower.includes("missing"))) {
    return {
      message: `Your file is missing one of the required sheets (${i18n.t("term.project")}s, Scenarios, ${i18n.t("term.scope")}s).`,
      actionHint: "Use the 'Generate template' button to download a valid template, fill it in, re-upload, then retry manually.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (lower.includes("invalid date") || lower.includes("datetimeparse") || lower.includes("expected format: yyyy-mm-dd")) {
    return {
      message: "One or more dates in the file aren't in the expected yyyy-mm-dd format.",
      actionHint: "Open the file, fix the date columns to use yyyy-mm-dd format (e.g. 2024-06-15), save, re-upload, then retry manually.",
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (lower.includes("project with code") && lower.includes("not found")) {
    return {
      message: `A ${i18n.t("term.scope")} row references a ${i18n.t("term.project")} Code that doesn't appear on the ${i18n.t("term.project")}s sheet.`,
      actionHint: `Check every ${i18n.t("term.scope")}'s '${i18n.t("term.project")} Code' column — each value must exactly match a code on the ${i18n.t("term.project")}s sheet. Correct mismatches, re-upload, then retry manually.`,
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (lower.includes("portfolio") && lower.includes("limit")) {
    return {
      message: `This operation would exceed your portfolio's ${i18n.t("term.block").toLowerCase()} limit.`,
      actionHint: `Reduce the quantities in your file or contact your administrator to raise the portfolio limit, then retry manually.`,
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (lower.includes("unauthorized") || lower.includes("401") || lower.includes("403")) {
    return {
      message: tr("progressNotifications.errors.permission.message", "You don't have permission to perform this action."),
      actionHint: tr(
        "progressNotifications.errors.permission.actionHint",
        "Contact your OFFOLIO administrator to request the required permissions, then retry manually."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (lower.includes("timeout") || lower.includes("econnaborted")) {
    return {
      message: tr(
        "progressNotifications.errors.timeout.message",
        "The server took too long to respond. The operation may still be running in the background."
      ),
      actionHint: tr(
        "progressNotifications.errors.timeout.actionHint",
        "Wait a minute and refresh the page to check the result. If the data hasn't changed, retry the operation."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }
  if (lower.includes("network") || lower.includes("failed to fetch")) {
    return {
      message: tr("progressNotifications.errors.network.message", "The connection to the server was lost during the operation."),
      actionHint: tr(
        "progressNotifications.errors.network.actionHint",
        "Check your network connection and retry. If you're on a VPN, verify it's still active."
      ),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  // Short clean backend message — pass through as-is
  if (raw.length < 160 && !raw.includes("Exception") && !raw.includes("\n")) {
    return {
      message: raw,
      actionHint: applyDiscreetManualRetry(fallback.actionHint),
      raw,
      supportUrl: FRESHDESK_SUPPORT_URL,
    };
  }

  return {
    ...fallback,
    actionHint: applyDiscreetManualRetry(fallback.actionHint),
  };
}
